import type { IdeaChatPhase } from './useIdeaChat';

export const APPLY_FORMAT_FLAG_KEY = 'apply.format.enabled' as const;

export const NOT_SET_FORMAT_TYPE = 'not_set' as const;
export const NOT_SET_FORMAT_OPTION_ID = 'not_set' as const;

export type FormatOptionId =
  | 'direct_30s'
  | 'direct_60s'
  | 'direct_3m'
  | 'broll_short_text'
  | 'broll_long_text'
  | 'broll_30s_voiceover'
  | 'broll_60s_voiceover'
  | 'carousel_4'
  | 'carousel_8'
  | 'carousel_12'
  | 'single_image_post'
  | 'screen_demo_30s'
  | 'screen_demo_60s'
  | 'screen_demo_3m'
  | 'conversation_30s'
  | 'conversation_60s'
  | 'conversation_3m'
  | 'reaction_30s'
  | 'reaction_60s'
  | 'reaction_3m'
  | 'skit_trend_beat'
  | 'skit_30s'
  | 'skit_60s'
  | typeof NOT_SET_FORMAT_OPTION_ID;

export type FormatType =
  | 'direct_to_camera'
  | 'broll_text_on_screen'
  | 'broll_voiceover'
  | 'carousel'
  | 'single_image'
  | 'screen_demo'
  | 'conversation_interview'
  | 'reaction_commentary'
  | 'skit_performance_trend'
  | typeof NOT_SET_FORMAT_TYPE;

/**
 * Machine-readable counterpart to `targetLength`. Mirrors
 * `apps/backend/src/items/idea-chat/format-options.ts` — the backend copy is
 * authoritative (it is what the prompt and post-generation validation use);
 * this copy exists so the length chip and Shorter/Longer controls can render
 * without a round trip. `useFormatOptions.spec.ts` pins the two together.
 */
export type FormatBudgetUnit = 'spoken_words' | 'onscreen_words' | 'slides' | 'none';

export interface FormatBudget {
  unit: FormatBudgetUnit;
  min: number;
  max: number;
  approxSeconds?: number;
}

export type LengthAdjustment = 'shorter' | 'longer';

export interface FormatOption {
  id: FormatOptionId;
  label: string;
  targetLength: string;
  budget: FormatBudget;
  isDefault?: boolean;
  skippedPhases?: IdeaChatPhase[];
  slideCount?: number;
}

export interface FormatConfig {
  id: FormatType;
  label: string;
  description: string;
  generationEffect: string;
  defaultOptionId: FormatOptionId;
  options: FormatOption[];
  skippedPhases?: IdeaChatPhase[];
  usesCarouselSlides?: boolean;
}

const TEXT_ONLY_BROLL_SKIPPED_PHASES: IdeaChatPhase[] = [
  'framework',
  'payoff',
  'hookSpoken',
  'scriptNotes',
  'editingNotes'
];

export const FORMAT_OPTIONS: FormatConfig[] = [
  {
    id: 'direct_to_camera',
    label: 'Talking Head / Yapping',
    description:
      'Single-speaker spoken script for talking head, teaching, storytime, rankings, or opinion.',
    generationEffect: 'Controls spoken word count, number of points, pacing, and depth.',
    defaultOptionId: 'direct_60s',
    options: [
      {
        id: 'direct_30s',
        label: '30 seconds',
        targetLength: '75-100 spoken words',
        budget: { unit: 'spoken_words', min: 75, max: 100, approxSeconds: 30 }
      },
      {
        id: 'direct_60s',
        label: '60 seconds',
        targetLength: '130-170 spoken words',
        budget: { unit: 'spoken_words', min: 130, max: 170, approxSeconds: 60 },
        isDefault: true
      },
      {
        id: 'direct_3m',
        label: '3 minutes',
        targetLength: '375-500 spoken words',
        budget: { unit: 'spoken_words', min: 375, max: 500, approxSeconds: 180 }
      }
    ]
  },
  {
    id: 'broll_text_on_screen',
    label: 'B-roll + text-on-screen',
    description: 'On-screen text over supporting footage. Nobody speaks.',
    generationEffect: 'Controls how much of the message the on-screen text carries.',
    defaultOptionId: 'broll_short_text',
    skippedPhases: TEXT_ONLY_BROLL_SKIPPED_PHASES,
    options: [
      {
        id: 'broll_short_text',
        label: 'Short text-only',
        targetLength: '5-20 on-screen words total',
        budget: { unit: 'onscreen_words', min: 5, max: 20 },
        isDefault: true
      },
      {
        id: 'broll_long_text',
        label: 'Long text-only',
        targetLength: '25-70 on-screen words total',
        budget: { unit: 'onscreen_words', min: 25, max: 70 }
      }
    ]
  },
  {
    id: 'broll_voiceover',
    label: 'B-roll + voiceover',
    description:
      'A spoken voiceover over supporting footage. No visible speaker required; an on-screen text hook may sit alongside the voiceover.',
    generationEffect: 'Controls spoken word count, pacing, and depth.',
    defaultOptionId: 'broll_30s_voiceover',
    options: [
      {
        id: 'broll_30s_voiceover',
        label: '30-second voiceover',
        targetLength: '65-90 spoken words',
        budget: { unit: 'spoken_words', min: 65, max: 90, approxSeconds: 30 },
        isDefault: true
      },
      {
        id: 'broll_60s_voiceover',
        label: '60-second voiceover',
        targetLength: '130-170 spoken words',
        budget: { unit: 'spoken_words', min: 130, max: 170, approxSeconds: 60 }
      }
    ]
  },
  {
    id: 'carousel',
    label: 'Carousel',
    description: 'Slide-by-slide post copy with slide headlines and body copy.',
    generationEffect:
      'Controls slide count, idea depth, amount of copy per slide, and CTA placement.',
    defaultOptionId: 'carousel_8',
    usesCarouselSlides: true,
    options: [
      {
        id: 'carousel_4',
        label: '4 slides',
        targetLength: '4 slides',
        budget: { unit: 'slides', min: 4, max: 4 },
        slideCount: 4
      },
      {
        id: 'carousel_8',
        label: '8 slides',
        targetLength: '8 slides',
        budget: { unit: 'slides', min: 8, max: 8 },
        isDefault: true,
        slideCount: 8
      },
      {
        id: 'carousel_12',
        label: '12 slides',
        targetLength: '12 slides',
        budget: { unit: 'slides', min: 12, max: 12 },
        slideCount: 12
      }
    ]
  },
  {
    id: 'single_image',
    label: 'Single image',
    description:
      'One static image where the visual carries the emotion and on-screen text plus caption carry the message.',
    generationEffect:
      'Controls image concept direction, on-screen text wording, and caption copy. No spoken script.',
    defaultOptionId: 'single_image_post',
    skippedPhases: TEXT_ONLY_BROLL_SKIPPED_PHASES,
    options: [
      {
        id: 'single_image_post',
        label: 'Single image',
        targetLength: 'One image + 5-30 on-screen words',
        budget: { unit: 'onscreen_words', min: 5, max: 30 },
        isDefault: true
      }
    ]
  },
  {
    id: 'screen_demo',
    label: 'Screen demo / walkthrough',
    description: 'Instructional narration matched to visible product or screen actions.',
    generationEffect:
      'Controls number of steps, narration depth, and whether the output needs detailed screen actions.',
    defaultOptionId: 'screen_demo_60s',
    options: [
      {
        id: 'screen_demo_30s',
        label: '30-second quick demo',
        targetLength: '65-90 spoken words',
        budget: { unit: 'spoken_words', min: 65, max: 90, approxSeconds: 30 }
      },
      {
        id: 'screen_demo_60s',
        label: '60-second walkthrough',
        targetLength: '130-180 spoken words',
        budget: { unit: 'spoken_words', min: 130, max: 180, approxSeconds: 60 },
        isDefault: true
      },
      {
        id: 'screen_demo_3m',
        label: '3-minute deep walkthrough',
        targetLength: '375-550 spoken words',
        budget: { unit: 'spoken_words', min: 375, max: 550, approxSeconds: 180 }
      }
    ]
  },
  {
    id: 'conversation_interview',
    label: 'Conversation / interview',
    description: 'Labeled dialogue with interviewer/interviewee or balanced conversational turns.',
    generationEffect:
      'Controls number of turns, balance of speakers, and whether the output is Q&A or fuller discussion.',
    defaultOptionId: 'conversation_60s',
    options: [
      {
        id: 'conversation_30s',
        label: '30-second clip',
        targetLength: '70-110 total spoken words',
        budget: { unit: 'spoken_words', min: 70, max: 110, approxSeconds: 30 }
      },
      {
        id: 'conversation_60s',
        label: '60-second exchange',
        targetLength: '140-220 total spoken words',
        budget: { unit: 'spoken_words', min: 140, max: 220, approxSeconds: 60 },
        isDefault: true
      },
      {
        id: 'conversation_3m',
        label: '3-minute interview segment',
        targetLength: '400-650 total spoken words',
        budget: { unit: 'spoken_words', min: 400, max: 650, approxSeconds: 180 }
      }
    ]
  },
  {
    id: 'reaction_commentary',
    label: 'Reaction / commentary',
    description:
      'One speaker responds to an external source asset, post, video, article, screenshot, or comment.',
    generationEffect:
      'Controls depth of critique, amount of source context, and conclusion/CTA length.',
    defaultOptionId: 'reaction_60s',
    options: [
      {
        id: 'reaction_30s',
        label: '30-second hot take',
        targetLength: '70-100 spoken words',
        budget: { unit: 'spoken_words', min: 70, max: 100, approxSeconds: 30 }
      },
      {
        id: 'reaction_60s',
        label: '60-second breakdown',
        targetLength: '130-180 spoken words',
        budget: { unit: 'spoken_words', min: 130, max: 180, approxSeconds: 60 },
        isDefault: true
      },
      {
        id: 'reaction_3m',
        label: '3-minute analysis',
        targetLength: '375-550 spoken words',
        budget: { unit: 'spoken_words', min: 375, max: 550, approxSeconds: 180 }
      }
    ]
  },
  {
    id: 'skit_performance_trend',
    label: 'Skit / performance / trend',
    description: 'Scene, roleplay, or performance where behavior and contrast reveal the idea.',
    generationEffect:
      'Controls character beats, dialogue density, and how much visual direction to include.',
    defaultOptionId: 'skit_30s',
    options: [
      {
        id: 'skit_trend_beat',
        label: '7-15 second trend beat',
        targetLength: '0-40 spoken words',
        budget: { unit: 'spoken_words', min: 0, max: 40, approxSeconds: 15 }
      },
      {
        id: 'skit_30s',
        label: '30-second skit',
        targetLength: '40-100 spoken words',
        budget: { unit: 'spoken_words', min: 40, max: 100, approxSeconds: 30 },
        isDefault: true
      },
      {
        id: 'skit_60s',
        label: '60-second scene',
        targetLength: '100-180 spoken words',
        budget: { unit: 'spoken_words', min: 100, max: 180, approxSeconds: 60 }
      }
    ]
  },
  {
    id: NOT_SET_FORMAT_TYPE,
    label: 'Not set',
    description: 'Format has not been chosen yet.',
    generationEffect: 'No generation constraints until a real format is selected.',
    defaultOptionId: NOT_SET_FORMAT_OPTION_ID,
    options: [
      {
        id: NOT_SET_FORMAT_OPTION_ID,
        label: 'Not set',
        targetLength: 'n/a',
        budget: { unit: 'none', min: 0, max: 0 },
        isDefault: true
      }
    ]
  }
];

/** Picker formats shown in Idea Chat cards (excludes Not set). */
export const SELECTABLE_FORMAT_OPTIONS = FORMAT_OPTIONS.filter(
  format => format.id !== NOT_SET_FORMAT_TYPE
);

/* -------------------------------------------------------------------------
 * Legacy hybrid compatibility (GH #1929) — mirrors
 * `apps/backend/src/items/idea-chat/format-options.ts`, which is authoritative.
 * `broll_text_voiceover` is never written or rendered; it only stays readable,
 * and the persisted option id names its successor exactly.
 * ---------------------------------------------------------------------- */

export const LEGACY_HYBRID_FORMAT_TYPE = 'broll_text_voiceover' as const;

export const BROLL_FORMAT_TYPES: readonly FormatType[] = [
  'broll_text_on_screen',
  'broll_voiceover'
] as const;

/** Thin-evidence fallback: the only side that can never grow a spoken script. */
export const LEGACY_HYBRID_FALLBACK_FORMAT_TYPE: FormatType = 'broll_text_on_screen';

const LEGACY_BVF_FORMAT_TO_FORMAT_TYPE: Record<string, FormatType> = {
  'Storytime / Personal Narratives': 'direct_to_camera',
  'Skit / Comedy Sketch': 'skit_performance_trend',
  'POV (Point of View)': 'direct_to_camera',
  'Vlog / Day-in-the-Life': 'direct_to_camera',
  'Tutorial / How-To': 'direct_to_camera',
  'Listicle / Tips': 'direct_to_camera',
  'Trend / Lip Sync': 'skit_performance_trend',
  'Voiceover Montages': 'broll_voiceover',
  'Reaction / Duet Style': 'reaction_commentary',
  'Unboxing / Reviews': 'direct_to_camera',
  'Behind-the-Scenes (BTS)': 'direct_to_camera',
  'Before & After / Transformation': 'broll_text_on_screen',
  'Social Experiments / Public Interactions': 'direct_to_camera',
  'Cinematic / Aesthetic Edits': 'broll_text_on_screen',
  'Podcast / Interview Clip': 'conversation_interview',
  'Yapping / Talking Head': 'direct_to_camera',
  'B-Roll + Text-On-Screen': 'broll_text_on_screen',
  'B-roll + voiceover': 'broll_voiceover'
};

function collapseKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const PICKER_FORMAT_BY_KEY = new Map<string, FormatType>();
for (const format of SELECTABLE_FORMAT_OPTIONS) {
  PICKER_FORMAT_BY_KEY.set(collapseKey(format.id), format.id);
  PICKER_FORMAT_BY_KEY.set(collapseKey(format.label), format.id);
}

/** The retired hybrid id and its retired picker label, collapsed. */
const RETIRED_HYBRID_KEYS = new Set<string>([
  collapseKey(LEGACY_HYBRID_FORMAT_TYPE),
  collapseKey('B-roll + text-on-screen / voiceover')
]);

const LEGACY_BVF_FORMAT_BY_KEY = new Map<string, FormatType>();
for (const [label, formatType] of Object.entries(LEGACY_BVF_FORMAT_TO_FORMAT_TYPE)) {
  LEGACY_BVF_FORMAT_BY_KEY.set(collapseKey(label), formatType);
}

export function isFormatUnset(formatType: string | null | undefined): boolean {
  if (formatType == null) return true;
  const trimmed = formatType.trim();
  if (!trimmed) return true;
  return trimmed.toLowerCase() === NOT_SET_FORMAT_TYPE;
}

export function unsetFormatFields(): {
  formatType: typeof NOT_SET_FORMAT_TYPE;
  formatOptionId: typeof NOT_SET_FORMAT_OPTION_ID;
} {
  return {
    formatType: NOT_SET_FORMAT_TYPE,
    formatOptionId: NOT_SET_FORMAT_OPTION_ID
  };
}

export function normalizeDetectedFormat(rawFormat: string | null | undefined): FormatType | null {
  const trimmed = rawFormat?.trim();
  if (!trimmed) return null;
  if (trimmed.toLowerCase() === NOT_SET_FORMAT_TYPE || collapseKey(trimmed) === 'not set') {
    return NOT_SET_FORMAT_TYPE;
  }

  const key = collapseKey(trimmed);
  const pickerMatch = PICKER_FORMAT_BY_KEY.get(key);
  if (pickerMatch) return pickerMatch;

  // The retired hybrid, by id or by its old picker label: on a string alone it
  // can only take the conservative side. A caller with an option id resolves it
  // precisely via `canonicalFormatType`.
  if (RETIRED_HYBRID_KEYS.has(key)) return LEGACY_HYBRID_FALLBACK_FORMAT_TYPE;

  const legacyExact = LEGACY_BVF_FORMAT_BY_KEY.get(key);
  if (legacyExact) return legacyExact;

  // Order matters: "b roll voiceover" contains "b roll".
  if (key.includes('b roll') || key.includes('text on screen')) {
    return key.includes('voiceover') || key.includes('voice over')
      ? 'broll_voiceover'
      : 'broll_text_on_screen';
  }
  if (key.includes('carousel')) return 'carousel';
  if (
    key.includes('single image') ||
    key.includes('image post') ||
    key.includes('photo post') ||
    key.includes('static image')
  ) {
    return 'single_image';
  }
  if (key.includes('screen') || key.includes('walkthrough') || key.includes('demo')) {
    return 'screen_demo';
  }
  if (key.includes('conversation') || key.includes('interview') || key.includes('podcast')) {
    return 'conversation_interview';
  }
  if (
    key.includes('reaction') ||
    key.includes('commentary') ||
    key.includes('duet') ||
    key.includes('green screen')
  ) {
    return 'reaction_commentary';
  }
  if (
    key.includes('skit') ||
    key.includes('performance') ||
    key.includes('lip sync') ||
    key.includes('trend')
  ) {
    return 'skit_performance_trend';
  }
  if (key.includes('voiceover') || key.includes('montage')) {
    return 'broll_voiceover';
  }
  if (
    key.includes('cinematic') ||
    key.includes('aesthetic') ||
    key.includes('before after') ||
    key.includes('transformation')
  ) {
    return 'broll_text_on_screen';
  }
  if (
    key.includes('storytime') ||
    key.includes('personal narrative') ||
    key.includes('pov') ||
    key.includes('point of view') ||
    key.includes('vlog') ||
    key.includes('day in the life') ||
    key.includes('tutorial') ||
    key.includes('how to') ||
    key.includes('listicle') ||
    key.includes('unboxing') ||
    key.includes('review') ||
    key.includes('yapping') ||
    key.includes('talking head') ||
    key.includes('behind the scenes') ||
    key.includes('bts') ||
    key.includes('social experiment') ||
    key.includes('direct')
  ) {
    return 'direct_to_camera';
  }
  return null;
}

export function formatLabelForDisplay(
  rawFormat: string | null | undefined,
  optionId?: string | null
): string {
  if (isFormatUnset(rawFormat)) return 'Not set';
  // A stored option id is exact evidence; a raw label is only a best guess.
  const canonical = canonicalFormatType(rawFormat, optionId);
  const formatType = canonical ?? normalizeDetectedFormat(rawFormat);
  if (!formatType || formatType === NOT_SET_FORMAT_TYPE) return 'Not set';
  return findFormat(formatType)?.label ?? 'Not set';
}

/**
 * Resolve any stored format id — current or legacy — to a live `FormatType`.
 * Pass `optionId` whenever it is available: it is what turns a legacy hybrid id
 * into the exact side the user chose instead of the conservative fallback.
 */
/** How a stored format id was resolved — provenance travels with the answer. */
export type FormatTypeResolutionSource = 'live' | 'legacy_option_id' | 'legacy_fallback';

export interface FormatTypeResolution {
  formatType: FormatType | null;
  source: FormatTypeResolutionSource;
}

/**
 * Resolve any stored format id — current or legacy — and say how.
 * `legacy_fallback` is the only outcome that can be wrong; the backend logs it
 * with the record id. See `docs/specs/formats/broll-split-migration.md`.
 */
export function resolveFormatTypeWithSource(
  formatType: string | null | undefined,
  optionId?: string | null
): FormatTypeResolution {
  if (!formatType) return { formatType: null, source: 'live' };
  const direct = FORMAT_OPTIONS.find(format => format.id === formatType);
  if (direct) return { formatType: direct.id, source: 'live' };
  if (collapseKey(formatType) !== collapseKey(LEGACY_HYBRID_FORMAT_TYPE)) {
    return { formatType: null, source: 'live' };
  }

  if (optionId) {
    const owner = FORMAT_OPTIONS.find(
      format =>
        BROLL_FORMAT_TYPES.includes(format.id) &&
        format.options.some(option => option.id === optionId)
    );
    if (owner) return { formatType: owner.id, source: 'legacy_option_id' };
  }
  return { formatType: LEGACY_HYBRID_FALLBACK_FORMAT_TYPE, source: 'legacy_fallback' };
}

export function canonicalFormatType(
  formatType: string | null | undefined,
  optionId?: string | null
): FormatType | null {
  return resolveFormatTypeWithSource(formatType, optionId).formatType;
}

/** True when the pair resolves by conservative guess rather than by evidence. */
export function isThinEvidenceFormatFallback(
  formatType: string | null | undefined,
  optionId?: string | null
): boolean {
  return resolveFormatTypeWithSource(formatType, optionId).source === 'legacy_fallback';
}

export function findFormat(
  formatType: string | null | undefined,
  optionId?: string | null
): FormatConfig | null {
  const canonical = canonicalFormatType(formatType, optionId);
  if (!canonical) return null;
  return FORMAT_OPTIONS.find(format => format.id === canonical) ?? null;
}

export function findFormatOption(
  formatType: string | null | undefined,
  optionId: string | null | undefined
): FormatOption | null {
  const format = findFormat(formatType, optionId);
  if (!format || !optionId) return null;
  return format.options.find(option => option.id === optionId) ?? null;
}

export function defaultFormatOptionId(
  formatType: string | null | undefined
): FormatOptionId | null {
  const format = findFormat(formatType);
  return format?.defaultOptionId ?? null;
}

export function skippedPhasesForFormat(
  formatType: string | null | undefined,
  optionId: string | null | undefined
): IdeaChatPhase[] {
  const format = findFormat(formatType, optionId);
  const option = findFormatOption(formatType, optionId);
  return [...(format?.skippedPhases ?? []), ...(option?.skippedPhases ?? [])];
}

export function usesCarouselSlides(formatType: string | null | undefined): boolean {
  return findFormat(formatType)?.usesCarouselSlides === true;
}

/** Fallback scale when a format has no adjacent option in the same unit. */
const BUDGET_SCALE_STEP = 0.3;

function scaleBudget(budget: FormatBudget, factor: number): FormatBudget {
  const scale = (value: number): number => Math.max(0, Math.round(value * factor));
  const min = scale(budget.min);
  const max = Math.max(min, scale(budget.max));
  return {
    unit: budget.unit,
    min: budget.max > 0 && max === 0 ? 0 : min,
    max: budget.max > 0 ? Math.max(1, max) : max,
    ...(budget.approxSeconds === undefined
      ? {}
      : { approxSeconds: Math.max(1, Math.round(budget.approxSeconds * factor)) })
  };
}

/**
 * Mirror of the backend `resolveBudget`. Returns null when the selection cannot
 * be resolved at all; the unset sentinel resolves to an explicit `none` unit.
 */
export function resolveBudget(
  formatType: string | null | undefined,
  formatOptionId: string | null | undefined,
  adjustment: LengthAdjustment | null = null
): FormatBudget | null {
  const format = findFormat(formatType, formatOptionId);
  const option = findFormatOption(formatType, formatOptionId);
  if (!format || !option) return null;

  const budget = option.budget;
  if (!adjustment || budget.unit === 'none') return budget;

  const sameUnit = format.options.filter(
    candidate => candidate.budget.unit === budget.unit && candidate.id !== option.id
  );
  const neighbour =
    adjustment === 'shorter'
      ? sameUnit
          .filter(candidate => candidate.budget.max < budget.max)
          .sort((a, b) => b.budget.max - a.budget.max)[0]
      : sameUnit
          .filter(candidate => candidate.budget.max > budget.max)
          .sort((a, b) => a.budget.max - b.budget.max)[0];

  if (neighbour) return neighbour.budget;
  return scaleBudget(
    budget,
    adjustment === 'shorter' ? 1 - BUDGET_SCALE_STEP : 1 + BUDGET_SCALE_STEP
  );
}
